import React, { useEffect, useMemo, useState } from "react";
import { Box, MenuItem, Select, TextField, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import {
  useEngagementTheme,
  engagementPageSx,
  EngagementCard,
  EngagementPill,
  engagementButtonSx,
  engagementSegSx,
} from "../components/engagementTheme";

const AUDIENCE_OPTIONS = ["School", "Corporate", "Housing society", "Other"];
const VIEWS = ["Board", "Partner directory", "Results"];

const EngagementCamps = () => {
  const t = useEngagementTheme();
  const [searchParams] = useSearchParams();
  const contextDoctorId = searchParams.get("doctorId") || "";

  const [view, setView] = useState("Board");
  const [city, setCity] = useState("All cities");
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [board, setBoard] = useState({ stages: [], lanes: [] });
  const [partners, setPartners] = useState([]);
  const [partnerSearch, setPartnerSearch] = useState("");
  const [results, setResults] = useState([]);
  const [resultsPage, setResultsPage] = useState(1);
  const [resultsTotalPages, setResultsTotalPages] = useState(1);
  const [toast, setToast] = useState("");

  const [formOpen, setFormOpen] = useState(!!contextDoctorId);
  const [fClinicQuery, setFClinicQuery] = useState("");
  const [fClinicOptions, setFClinicOptions] = useState([]);
  const [fClinic, setFClinic] = useState(null);
  const [fAudience, setFAudience] = useState("School");
  const [fOrigin, setFOrigin] = useState("Clinic asked");
  const [fArea, setFArea] = useState("");

  const [decliningId, setDecliningId] = useState("");
  const [declineReason, setDeclineReason] = useState("Too soon after the last camp");

  const [identifyOpen, setIdentifyOpen] = useState(false);
  const [identifyQuery, setIdentifyQuery] = useState("");
  const [partnerFormId, setPartnerFormId] = useState("");
  const [partnerName, setPartnerName] = useState("");
  const [partnerArea, setPartnerArea] = useState("");
  const [partnerContact, setPartnerContact] = useState("");

  const [saving, setSaving] = useState(false);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [savingNoteId, setSavingNoteId] = useState("");
  const Toast = useToast();
  const auth = localStorage.getItem("token");
  const authHeaders = { headers: { Authorization: `Bearer ${auth}` } };

  useEffect(() => {
    if (contextDoctorId) {
      axios
        .get(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/clinics/${contextDoctorId}`, authHeaders)
        .then((res) => setFClinic({ id: contextDoctorId, doctorName: res.data.data.doctorName, clinicName: res.data.data.clinicName }))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextDoctorId]);

  const loadBoardAndRequests = async (cityFilter) => {
    setLoading(true);
    try {
      const [reqRes, boardRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/camps/requests`, { ...authHeaders, params: { city: cityFilter } }),
        axios.get(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/camps/board`, { ...authHeaders, params: { city: cityFilter } }),
      ]);
      setRequests(reqRes.data.data || []);
      setBoard(boardRes.data.data || { stages: [], lanes: [] });
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not load screening camps.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBoardAndRequests(city);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city]);

  useEffect(() => {
    if (view === "Partner directory" && partners.length === 0) {
      axios
        .get(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/camps/partners`, authHeaders)
        .then((res) => setPartners(res.data.data || []))
        .catch((error) => Toast.error(error.response?.data?.message || "Could not load the partner directory."));
    }
    if (view === "Results") {
      axios
        .get(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/camps/results`, { ...authHeaders, params: { page: resultsPage, limit: 10 } })
        .then((res) => {
          setResults(res.data.data?.results || []);
          setResultsTotalPages(Math.max(1, res.data.data?.totalPages || 1));
        })
        .catch((error) => Toast.error(error.response?.data?.message || "Could not load camp results."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, resultsPage]);

  const filteredPartners = useMemo(() => {
    const q = partnerSearch.trim().toLowerCase();
    if (!q) return partners;
    return partners.filter((p) =>
      [p.name, p.type, p.area, p.contact, p.clinicName].some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [partners, partnerSearch]);

  const cityOptions = useMemo(() => {
    const set = new Set();
    requests.forEach((r) => r.city && set.add(r.city));
    return ["All cities", ...[...set].sort()];
  }, [requests]);

  const searchClinics = async (keyword) => {
    setFClinicQuery(keyword);
    setFClinic(null);
    if (!keyword || keyword.trim().length < 2) {
      setFClinicOptions([]);
      return;
    }
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/tickets/doctors-search`, { ...authHeaders, params: { keyword } });
      const doctors = res.data.doctors || [];
      const rows = doctors.flatMap((d) =>
        (d.clinics || []).length
          ? d.clinics.map((c) => ({ id: d._id, doctorName: d.name, clinicName: c.name }))
          : [{ id: d._id, doctorName: d.name, clinicName: "" }]
      );
      setFClinicOptions(rows);
    } catch {
      setFClinicOptions([]);
    }
  };

  const createCard = async () => {
    if (!fClinic || !fArea.trim()) return;
    setSaving(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/camps/cards`,
        { doctorId: fClinic.id, origin: fOrigin === "Clinic asked" ? "clinic_asked" : "scano_suggested", audience: fAudience, area: fArea.trim() },
        authHeaders
      );
      setToast(`Partner request created for ${fClinic.doctorName}.`);
      setFormOpen(false);
      setFClinic(null);
      setFClinicQuery("");
      setFArea("");
      loadBoardAndRequests(city);
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not create this request.");
    } finally {
      setSaving(false);
    }
  };

  const startSearch = async (requestId, clinicLabel) => {
    setSaving(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/camps/requests/${requestId}/start`, {}, authHeaders);
      setToast(`Partner search started for ${clinicLabel}. Card added to Brief agreed.`);
      loadBoardAndRequests(city);
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not start this search.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDecline = async (requestId, clinicLabel) => {
    setSaving(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/camps/requests/${requestId}/decline`, { reason: declineReason }, authHeaders);
      setToast(`Request declined. ${clinicLabel} gets a WhatsApp message with the reason.`);
      setDecliningId("");
      loadBoardAndRequests(city);
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not decline this request.");
    } finally {
      setSaving(false);
    }
  };

  const savePartnerDetails = async (cardId) => {
    if (!partnerName.trim()) return;
    setSaving(true);
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/camps/cards/${cardId}/partner`,
        { partnerName, partnerArea, partnerContact },
        authHeaders
      );
      setToast("Partner saved.");
      setIdentifyOpen(false);
      setIdentifyQuery("");
      setPartnerFormId("");
      setPartnerName("");
      setPartnerArea("");
      setPartnerContact("");
      loadBoardAndRequests(city);
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not save this partner.");
    } finally {
      setSaving(false);
    }
  };

  const allBoardCards = useMemo(
    () =>
      board.lanes.flatMap((l) =>
        l.cells.flatMap((cell, idx) => cell.cards.map((k) => ({ ...k, stageName: board.stages[idx]?.name || "" })))
      ),
    [board]
  );

  const saveNote = async (cardId, note) => {
    setSavingNoteId(cardId);
    try {
      await axios.patch(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/camps/cards/${cardId}/note`, { note }, authHeaders);
      await loadBoardAndRequests(city);
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not save this note.");
    } finally {
      setSavingNoteId("");
    }
  };

  const advance = async (cardId, clinicLabel, nextName) => {
    try {
      await axios.patch(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/camps/cards/${cardId}/advance`, {}, authHeaders);
      setToast(`${clinicLabel} moved to ${nextName}.`);
      loadBoardAndRequests(city);
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not move this card.");
    }
  };

  if (loading && !requests.length && !board.lanes.length) {
    return (
      <Box sx={{ ...engagementPageSx(t), position: "relative", minHeight: 300 }}>
        <PageLoader />
      </Box>
    );
  }

  return (
    <Box sx={engagementPageSx(t)}>
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-end", gap: "16px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.015em", color: t.textPrimary }}>Screening camps</Typography>
          <Typography sx={{ fontSize: 15, color: t.textMuted }}>
            We find the right school, corporate or society and make the first introduction. The clinic runs the camp; we share the digital kit.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-end" }}>
          <Box role="group" aria-label="View" sx={{ display: "flex", border: `1px solid ${t.disabledBorder}`, borderRadius: "8px", overflow: "hidden" }}>
            {VIEWS.map((v) => (
              <Box key={v} component="button" type="button" onClick={() => setView(v)} sx={engagementSegSx(t, view === v)}>{v}</Box>
            ))}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography component="label" htmlFor="city" sx={{ fontSize: 13, color: t.textMuted }}>City</Typography>
            <Select id="city" size="small" value={city} onChange={(e) => setCity(e.target.value)} sx={{ height: 44, minWidth: 150, bgcolor: t.inputBg }}>
              {cityOptions.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => {
              setIdentifyOpen((o) => !o);
              setView("Board");
              setPartnerFormId("");
              setIdentifyQuery("");
            }}
            sx={{ ...engagementButtonSx(t), border: `1px solid ${t.disabledBorder}`, color: t.textPrimary }}
          >
            {identifyOpen ? "Close" : "Identify partner"}
          </Box>
          <Box component="button" type="button" onClick={() => { setFormOpen((o) => !o); setView("Board"); }} sx={engagementButtonSx(t, "solid")}>
            {formOpen ? "Close" : "New partner request"}
          </Box>
        </Box>
      </Box>

      {toast && (
        <Box role="status" sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "12px", padding: "14px 18px", borderRadius: "12px", bgcolor: t.tone.ok.bg, color: t.tone.ok.color, fontSize: 15, fontWeight: 600 }}>
          <span>{toast}</span>
          <Box component="button" type="button" onClick={() => setToast("")} sx={{ minHeight: 44, padding: "0 14px", border: `1px solid ${t.tone.ok.color}`, borderRadius: "8px", bgcolor: "transparent", color: t.tone.ok.color, font: "inherit", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            Dismiss
          </Box>
        </Box>
      )}

      {formOpen && (
        <EngagementCard t={t} sx={{ border: `2px solid ${t.accent}` }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>New partner request</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "flex-end" }}>
            <Box sx={{ flex: "1 1 260px", display: "flex", flexDirection: "column", gap: "4px" }}>
              <TextField label="Clinic owner and clinic" value={fClinicQuery} onChange={(e) => searchClinics(e.target.value)} sx={{ bgcolor: t.inputBg }} />
              {!fClinic && fClinicOptions.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  {fClinicOptions.map((o, i) => (
                    <Box key={`${o.id}-${i}`} onClick={() => { setFClinic(o); setFClinicQuery(`${o.doctorName}, ${o.clinicName}`); setFClinicOptions([]); }} sx={{ cursor: "pointer", padding: "6px 10px", borderRadius: "8px", bgcolor: t.hoverBg, fontSize: 14 }}>
                      {o.doctorName}{o.clinicName ? `, ${o.clinicName}` : ""}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography component="label" htmlFor="aud" sx={{ fontSize: 13, color: t.textMuted }}>Audience</Typography>
              <Select id="aud" size="small" value={fAudience} onChange={(e) => setFAudience(e.target.value)} sx={{ height: 44, minWidth: 150, bgcolor: t.inputBg }}>
                {AUDIENCE_OPTIONS.map((a) => (
                  <MenuItem key={a} value={a}>{a}</MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography component="label" htmlFor="origin" sx={{ fontSize: 13, color: t.textMuted }}>Started by</Typography>
              <Select id="origin" size="small" value={fOrigin} onChange={(e) => setFOrigin(e.target.value)} sx={{ height: 44, minWidth: 160, bgcolor: t.inputBg }}>
                <MenuItem value="Clinic asked">Clinic asked</MenuItem>
                <MenuItem value="scanO suggested">scanO suggested</MenuItem>
              </Select>
            </Box>
            <TextField label="Area" value={fArea} onChange={(e) => setFArea(e.target.value)} sx={{ bgcolor: t.inputBg, minWidth: 220, flex: "1 1 220px" }} />
          </Box>
          <Box sx={{ display: "flex", gap: "10px" }}>
            <Box component="button" type="button" disabled={saving || !fClinic || !fArea.trim()} onClick={createCard} sx={{ ...engagementButtonSx(t, "solid"), opacity: saving || !fClinic || !fArea.trim() ? 0.6 : 1 }}>
              {saving ? "Saving…" : "Create request"}
            </Box>
            <Box component="button" type="button" onClick={() => setFormOpen(false)} sx={{ ...engagementButtonSx(t), border: 0 }}>Cancel</Box>
          </Box>
        </EngagementCard>
      )}

      {identifyOpen && (
        <EngagementCard t={t} sx={{ border: `2px solid ${t.accent}` }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Identify partner</Typography>
          {!partnerFormId && (
            <>
              <TextField label="Search clinic on the board" value={identifyQuery} onChange={(e) => setIdentifyQuery(e.target.value)} sx={{ bgcolor: t.inputBg }} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", maxHeight: 280, overflowY: "auto" }}>
                {allBoardCards.length === 0 && <Typography sx={{ fontSize: 14, color: t.textMuted }}>No cards on the board yet.</Typography>}
                {allBoardCards
                  .filter((k) => k.clinic.toLowerCase().includes(identifyQuery.toLowerCase()))
                  .map((k) => (
                    <Box
                      key={k.id}
                      onClick={() => { setPartnerFormId(k.id); setPartnerName(k.partnerName || ""); setPartnerArea(""); setPartnerContact(""); }}
                      sx={{ cursor: "pointer", padding: "8px 10px", borderRadius: "8px", bgcolor: t.hoverBg, display: "flex", justifyContent: "space-between", gap: "10px" }}
                    >
                      <span>{k.clinic} <Typography component="span" sx={{ fontSize: 13, color: t.textMuted }}>({k.stageName})</Typography></span>
                      <Typography sx={{ fontSize: 13, color: t.textMuted }}>{k.partnerName ? `Current: ${k.partnerName}` : "No partner yet"}</Typography>
                    </Box>
                  ))}
              </Box>
            </>
          )}
          {partnerFormId && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <Typography sx={{ fontSize: 14, color: t.textMuted }}>
                {allBoardCards.find((k) => k.id === partnerFormId)?.clinic}
                {partnerName && ` — already has a partner; this will replace it.`}
              </Typography>
              <TextField size="small" label="Partner name" value={partnerName} onChange={(e) => setPartnerName(e.target.value)} sx={{ bgcolor: t.inputBg }} />
              <TextField size="small" label="Contact" value={partnerContact} onChange={(e) => setPartnerContact(e.target.value)} sx={{ bgcolor: t.inputBg }} />
              <TextField size="small" label="Area" value={partnerArea} onChange={(e) => setPartnerArea(e.target.value)} sx={{ bgcolor: t.inputBg }} />
              <Box sx={{ display: "flex", gap: "10px" }}>
                <Box component="button" type="button" disabled={saving || !partnerName.trim()} onClick={() => savePartnerDetails(partnerFormId)} sx={{ ...engagementButtonSx(t, "solid"), opacity: saving || !partnerName.trim() ? 0.6 : 1 }}>
                  {saving ? "Saving…" : "Save"}
                </Box>
                <Box component="button" type="button" onClick={() => setPartnerFormId("")} sx={{ ...engagementButtonSx(t), border: 0 }}>Back</Box>
              </Box>
            </Box>
          )}
        </EngagementCard>
      )}

      {view === "Board" && (
        <>
          <EngagementCard t={t}>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "10px" }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Clinics asking for a camp partner</Typography>
              <Typography sx={{ fontSize: 14, color: t.textMuted }}>Reply within 2 working days, start the partner search within 5</Typography>
            </Box>
            {requests.length === 0 && <Typography sx={{ fontSize: 14, color: t.textMuted }}>No requests waiting.</Typography>}
            {requests.map((q) => (
              <Box key={q.id} sx={{ display: "flex", flexDirection: "column", gap: "10px", pt: "12px", borderTop: `1px solid ${t.borderMuted}` }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "14px" }}>
                  <Box sx={{ flex: "1 1 260px", minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 600 }}>{q.clinic}</Typography>
                    <Typography sx={{ fontSize: 14, color: t.textSecondary }}>{q.ask}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13, color: t.textMuted, width: 150 }}>{q.source}</Typography>
                  <EngagementPill t={t} tone={q.slaTone}>{q.slaLabel}</EngagementPill>
                  <Box sx={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
                    <Box component="button" type="button" onClick={() => startSearch(q.id, q.clinic)} sx={engagementButtonSx(t)}>Start partner search</Box>
                    <Box component="button" type="button" onClick={() => setDecliningId(decliningId === q.id ? "" : q.id)} sx={{ ...engagementButtonSx(t), border: `1px solid ${t.disabledBorder}`, color: t.textPrimary }}>Decline with reason</Box>
                  </Box>
                </Box>
                {decliningId === q.id && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-end", padding: "12px", bgcolor: t.hoverBg, borderRadius: "10px" }}>
                    <Select size="small" value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} sx={{ height: 44, minWidth: 220, bgcolor: t.inputBg }}>
                      <MenuItem value="Too soon after the last camp">Too soon after the last camp</MenuItem>
                      <MenuItem value="Outside our coverage">Outside our coverage</MenuItem>
                      <MenuItem value="No suitable partners nearby">No suitable partners nearby</MenuItem>
                    </Select>
                    <Box component="button" type="button" onClick={() => confirmDecline(q.id, q.clinic)} sx={{ ...engagementButtonSx(t), border: `1px solid ${t.disabledBorder}`, color: t.textPrimary }}>Confirm decline</Box>
                    <Typography sx={{ fontSize: 13, color: t.textMuted }}>The clinic gets a WhatsApp message with the reason.</Typography>
                  </Box>
                )}
              </Box>
            ))}
          </EngagementCard>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "baseline", gap: "10px" }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Partner introductions</Typography>
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>Moving a card to Camp scheduled raises a diagnostics ticket for Air clinics</Typography>
            </Box>
            <Box sx={{ overflowX: "auto", bgcolor: t.cardBg, border: `1px solid ${t.border}`, borderRadius: "14px" }}>
              <Box sx={{ minWidth: 1240, display: "flex", flexDirection: "column" }}>
                <Box sx={{ display: "flex", borderBottom: `1px solid ${t.border}` }}>
                  <Box sx={{ flex: "0 0 170px", padding: "12px 16px", fontSize: 13, color: t.textMuted }}>Lane</Box>
                  {board.stages.map((s) => (
                    <Box key={s.name} sx={{ flex: "1 1 0", minWidth: 0, padding: "12px", fontSize: 14, fontWeight: 700, borderLeft: `1px solid ${t.borderMuted}` }}>
                      {s.name} <Box component="span" sx={{ fontWeight: 500, color: t.textMuted }}>{s.count}</Box>
                    </Box>
                  ))}
                </Box>
                {board.lanes.map((l) => (
                  <Box key={l.name} sx={{ display: "flex", borderBottom: `1px solid ${t.border}` }}>
                    <Box sx={{ flex: "0 0 170px", boxSizing: "border-box", padding: "14px 16px", display: "flex", flexDirection: "column", gap: "6px" }}>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex", alignItems: "center", height: "24px", px: "9px", borderRadius: "12px",
                          fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", alignSelf: "flex-start",
                          bgcolor: l.tone === "req" ? t.lane.req.bg : t.lane.ini.bg,
                          color: l.tone === "req" ? t.lane.req.color : t.lane.ini.color,
                        }}
                      >
                        {l.name}
                      </Box>
                      <Typography sx={{ fontSize: 13, color: t.textMuted }}>{l.rule}</Typography>
                    </Box>
                    {l.cells.map((cell, idx) => (
                      <Box key={idx} sx={{ flex: "1 1 0", minWidth: 0, padding: "10px", display: "flex", flexDirection: "column", gap: "10px", borderLeft: `1px solid ${t.borderMuted}`, bgcolor: t.cellBg }}>
                        {cell.cards.map((k) => (
                          <Box key={k.id} sx={{ bgcolor: t.cardBg, border: `1px solid ${t.border}`, borderRadius: "10px", padding: "12px", display: "flex", flexDirection: "column", gap: "6px" }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{k.clinic}</Typography>
                            <Typography sx={{ fontSize: 13, color: t.textSecondary }}>{k.partner}</Typography>
                            {idx === 1 || idx === 2 || idx === 3 ? (
                              <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                <TextField
                                  size="small"
                                  multiline
                                  minRows={2}
                                  placeholder="Add a note…"
                                  value={noteDrafts[k.id] ?? k.progress ?? ""}
                                  onChange={(e) => setNoteDrafts((d) => ({ ...d, [k.id]: e.target.value }))}
                                  sx={{ bgcolor: t.inputBg }}
                                />
                                <Box
                                  component="button"
                                  type="button"
                                  disabled={savingNoteId === k.id || (noteDrafts[k.id] ?? k.progress ?? "") === (k.progress || "")}
                                  onClick={() => saveNote(k.id, noteDrafts[k.id] ?? "")}
                                  sx={{
                                    alignSelf: "flex-start", minHeight: 32, padding: "0 10px",
                                    border: `1px solid ${t.disabledBorder}`, borderRadius: "8px",
                                    bgcolor: t.cardBg, color: t.textPrimary, font: "inherit", fontSize: 12, fontWeight: 600,
                                    cursor: savingNoteId === k.id ? "not-allowed" : "pointer",
                                    opacity: (noteDrafts[k.id] ?? k.progress ?? "") === (k.progress || "") ? 0.6 : 1,
                                  }}
                                >
                                  {savingNoteId === k.id ? "Saving…" : "Save note"}
                                </Box>
                              </Box>
                            ) : (
                              <Typography sx={{ fontSize: 13, color: t.textMuted }}>{k.progress}</Typography>
                            )}
                            <Typography sx={{ fontSize: 13, color: t.textMuted }}>{k.partnerName ? `Partner: ${k.partnerName}` : "No partner yet"}</Typography>
                            {k.diag && <EngagementPill t={t} tone={k.diagTone}>{k.diag}</EngagementPill>}
                            <Typography sx={{ fontSize: 13, color: t.textSecondary }}>{k.status}</Typography>
                            {k.canMove && (
                              <Box component="button" type="button" onClick={() => advance(k.id, k.clinic, k.nextName)} sx={{ minHeight: 40, padding: "0 10px", border: `1px solid ${t.accent}`, borderRadius: "8px", bgcolor: t.cardBg, color: t.accent, font: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                                Move to {k.nextName}
                              </Box>
                            )}
                          </Box>
                        ))}
                        {cell.empty && <Typography sx={{ fontSize: 13, color: t.textMuted, padding: "4px 2px" }}>Nothing here</Typography>}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </>
      )}

      {view === "Partner directory" && (
        <>
        <Box sx={{ marginBottom: "12px" }}>
          <TextField
            size="small"
            placeholder="Search by partner, type, area, contact or clinic"
            value={partnerSearch}
            onChange={(e) => setPartnerSearch(e.target.value)}
            sx={{ width: 320, maxWidth: "100%", bgcolor: t.inputBg }}
          />
        </Box>
        <Box sx={{ overflowX: "auto", bgcolor: t.cardBg, border: `1px solid ${t.border}`, borderRadius: "14px" }}>
          <Box component="table" sx={{ width: "100%", minWidth: 900, borderCollapse: "collapse", fontSize: 14 }}>
            <Box component="thead">
              <Box component="tr" sx={{ textAlign: "left", color: t.textMuted, fontSize: 13 }}>
                {["Partner", "Type", "Area", "Contact", "Introduced to", "Outcome"].map((h) => (
                  <Box component="th" key={h} sx={{ padding: "12px 16px", borderBottom: `1px solid ${t.border}` }}>{h}</Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {filteredPartners.length === 0 && (
                <Box component="tr"><Box component="td" colSpan={6} sx={{ padding: "16px", color: t.textMuted }}>{partners.length === 0 ? "No partners identified yet — fill in a partner's name once one is found for a card." : "No partners match your search."}</Box></Box>
              )}
              {filteredPartners.map((p, i) => (
                <Box component="tr" key={i}>
                  <Box component="td" sx={{ padding: "12px 16px", borderBottom: `1px solid ${t.borderMuted}`, fontWeight: 600 }}>{p.name}</Box>
                  <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>{p.type}</Box>
                  <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>{p.area}</Box>
                  <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>{p.contact}</Box>
                  <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>{p.clinicName}</Box>
                  <Box component="td" sx={{ padding: "12px 16px", borderBottom: `1px solid ${t.borderMuted}` }}><EngagementPill t={t} tone={p.tone}>{p.outcome}</EngagementPill></Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
        </>
      )}

      {view === "Results" && (
        <>
        <Box sx={{ overflowX: "auto", bgcolor: t.cardBg, border: `1px solid ${t.border}`, borderRadius: "14px" }}>
          <Box component="table" sx={{ width: "100%", minWidth: 900, borderCollapse: "collapse", fontSize: 14 }}>
            <Box component="thead">
              <Box component="tr" sx={{ textAlign: "left", color: t.textMuted, fontSize: 13 }}>
                {["Clinic", "Camp", "Lane", "Scans after", "Recalls sent", "Usage, 30 days after vs before"].map((h) => (
                  <Box component="th" key={h} sx={{ padding: "12px 16px", borderBottom: `1px solid ${t.border}` }}>{h}</Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {results.length === 0 && (
                <Box component="tr"><Box component="td" colSpan={6} sx={{ padding: "16px", color: t.textMuted }}>No camps marked done yet.</Box></Box>
              )}
              {results.map((r, i) => (
                <Box component="tr" key={i}>
                  <Box component="td" sx={{ padding: "12px 16px", borderBottom: `1px solid ${t.borderMuted}`, fontWeight: 600 }}>{r.clinic}</Box>
                  <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>{r.camp}</Box>
                  <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>{r.lane}</Box>
                  <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}`, fontVariantNumeric: "tabular-nums" }}>{r.scansAfter}</Box>
                  <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}`, color: t.textMuted }}>Not tracked yet</Box>
                  <Box component="td" sx={{ padding: "12px 16px", borderBottom: `1px solid ${t.borderMuted}` }}><EngagementPill t={t} tone={r.tone}>{r.lift}</EngagementPill></Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
        {resultsTotalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
            <Typography sx={{ fontSize: 13, color: t.textMuted }}>Page {resultsPage} of {resultsTotalPages}</Typography>
            <Box sx={{ display: "flex", gap: "8px" }}>
              <Box
                component="button"
                type="button"
                disabled={resultsPage <= 1}
                onClick={() => setResultsPage((p) => Math.max(1, p - 1))}
                sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: resultsPage <= 1 ? "not-allowed" : "pointer" }}
              >
                Previous
              </Box>
              <Box
                component="button"
                type="button"
                disabled={resultsPage >= resultsTotalPages}
                onClick={() => setResultsPage((p) => Math.min(resultsTotalPages, p + 1))}
                sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: resultsPage >= resultsTotalPages ? "not-allowed" : "pointer" }}
              >
                Next
              </Box>
            </Box>
          </Box>
        )}
        </>
      )}
    </Box>
  );
};

export default EngagementCamps;
